import { useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList,
} from 'recharts';
import { Table2, ChartNoAxesColumn } from 'lucide-react';
import type { ReportResult } from '../../types/api';
import {
  seriesColor, AXIS_COLOR, GRID_COLOR, TEXT_MUTED,
  formatCell, tickFormatter, type ColumnType,
} from '../../lib/viz';

export type ChartType = 'stat' | 'line' | 'bar' | 'area' | 'pie' | 'donut' | 'table' | 'list' | 'gauge';

interface Props {
  result: ReportResult;
  chartType: ChartType;
  mapping?: { x?: string; y?: string; series?: string };
  height?: number;
}

/**
 * One renderer for every report. The backend describes its columns and their types;
 * a widget adds a chart type and a column mapping. That is what lets the AI create a
 * dashboard it has never seen the data for — it only has to name a report, a chart
 * type and which columns are the axes.
 *
 * Light-mode contrast relief: every chart carries a table view toggle and direct
 * labels, which is what the palette's contrast WARN requires.
 */
export function ReportChart({ result, chartType, mapping, height = 260 }: Props) {
  const [showTable, setShowTable] = useState(chartType === 'table');

  const currency = result.currency ?? 'GBP';
  const columns = result.columns ?? [];
  const rows = result.rows ?? [];

  const typeOf = (key?: string): ColumnType =>
    (columns.find((c) => c.key === key)?.type ?? 'number') as ColumnType;

  // Resolve axes: prefer the widget's mapping, else infer from column types.
  const xKey = mapping?.x
    ?? columns.find((c) => c.type === 'date')?.key
    ?? columns.find((c) => c.type === 'string')?.key
    ?? columns[0]?.key;

  /** Every numeric column except the x axis becomes a series. */
  const yKeys = useMemo(() => {
    if (mapping?.y) return [mapping.y];
    return columns
      .filter((c) => c.key !== xKey && ['number', 'money', 'percent'].includes(c.type))
      .map((c) => c.key)
      .slice(0, 8); // token ceiling — never generate a 9th hue
  }, [columns, mapping?.y, xKey]);

  const labelFor = (key: string) => columns.find((c) => c.key === key)?.label ?? key;
  const yType = typeOf(yKeys[0]);

  if (!rows.length) {
    return (
      <div className="grid place-items-center py-10 text-[12.5px] text-ink-400" style={{ height }}>
        No data for this period
      </div>
    );
  }

  // ── Table view ────────────────────────────────────────────────────────────
  const table = (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max text-[12.5px]">
        <thead>
          <tr className="bg-ink-50/60 text-left">
            {columns.map((c) => (
              <th key={c.key} className={`px-3 py-1.5 text-[11.5px] font-medium uppercase tracking-wide text-ink-500 ${c.type !== 'string' && c.type !== 'date' ? 'text-right' : ''}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 100).map((row, i) => (
            <tr key={i} className="border-t border-ink-100 hover:bg-ink-50/60">
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`px-3 py-1.5 text-ink-800 ${c.type !== 'string' && c.type !== 'date' ? 'text-right tabular-nums' : ''}`}
                >
                  {formatCell(row[c.key], c.type as ColumnType, currency)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {result.totals && (
          <tfoot>
            <tr className="border-t border-ink-100 font-medium">
              {columns.map((c, i) => (
                <td key={c.key} className={`px-3 py-2 ${i === 0 ? '' : 'text-right tabular-nums'}`}>
                  {i === 0 ? 'Total' : result.totals?.[c.key] !== undefined
                    ? formatCell(result.totals[c.key], c.type as ColumnType, currency)
                    : ''}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
      {rows.length > 100 && (
        <p className="px-3 py-2 text-[12px] text-ink-400">Showing first 100 of {rows.length} rows.</p>
      )}
    </div>
  );

  if (showTable || chartType === 'table' || chartType === 'list') {
    return (
      <div>
        {chartType !== 'table' && chartType !== 'list' && <ViewToggle showTable onToggle={() => setShowTable(false)} />}
        {table}
      </div>
    );
  }

  const axisProps = {
    stroke: AXIS_COLOR,
    tick: { fill: TEXT_MUTED, fontSize: 11 },
    tickLine: false,
    axisLine: { stroke: GRID_COLOR },
  };

  const tooltip = (
    <Tooltip
      cursor={{ stroke: GRID_COLOR, strokeWidth: 1 }}
      contentStyle={{
        borderRadius: 10,
        border: '1px solid var(--color-ink-200)',
        background: 'var(--color-white)',
        boxShadow: '0 12px 32px -8px rgba(33,32,32,0.18)',
        fontSize: 12,
        padding: '8px 10px',
      }}
      formatter={(value: unknown, name: unknown) => [
        formatCell(value, typeOf(String(name)), currency),
        labelFor(String(name)),
      ]}
      labelFormatter={(v) => formatCell(v, typeOf(xKey), currency)}
    />
  );

  // A legend is always present for 2+ series so identity is never colour alone.
  const legend = yKeys.length > 1
    ? <Legend
        verticalAlign="top"
        align="right"
        height={28}
        iconType="circle"
        iconSize={8}
        formatter={(v) => <span className="text-[12px] text-ink-600">{labelFor(String(v))}</span>}
      />
    : null;

  const chart = (() => {
    switch (chartType) {
      case 'line':
        return (
          <LineChart data={rows} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid stroke={GRID_COLOR} vertical={false} />
            <XAxis dataKey={xKey} {...axisProps} tickFormatter={tickFormatter(typeOf(xKey), currency)} />
            <YAxis {...axisProps} width={56} tickFormatter={tickFormatter(yType, currency)} />
            {tooltip}{legend}
            {yKeys.map((key, i) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={seriesColor(i)}
                strokeWidth={2}
                dot={{ r: 0 }}
                activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--color-white)' }}
              />
            ))}
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart data={rows} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <defs>
              {yKeys.map((key, i) => (
                <linearGradient key={key} id={`fill-${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={seriesColor(i)} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={seriesColor(i)} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke={GRID_COLOR} vertical={false} />
            <XAxis dataKey={xKey} {...axisProps} tickFormatter={tickFormatter(typeOf(xKey), currency)} />
            <YAxis {...axisProps} width={56} tickFormatter={tickFormatter(yType, currency)} />
            {tooltip}{legend}
            {yKeys.map((key, i) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={seriesColor(i)}
                strokeWidth={2}
                fill={`url(#fill-${key})`}
              />
            ))}
          </AreaChart>
        );

      case 'pie':
      case 'donut': {
        const valueKey = yKeys[0] ?? columns[1]?.key ?? 'value';
        return (
          <PieChart>
            {tooltip}
            <Pie
              data={rows}
              dataKey={valueKey}
              nameKey={xKey}
              innerRadius={chartType === 'donut' ? '55%' : 0}
              outerRadius="80%"
              paddingAngle={2}          /* 2px surface gap between adjacent fills */
              stroke="var(--color-white)"
              strokeWidth={2}
              label={(e: { name?: string }) => e.name ?? ''}
              labelLine={false}
            >
              {rows.map((_, i) => <Cell key={i} fill={seriesColor(i)} />)}
            </Pie>
          </PieChart>
        );
      }

      case 'bar':
      default:
        return (
          <BarChart data={rows} margin={{ top: 12, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid stroke={GRID_COLOR} vertical={false} />
            <XAxis dataKey={xKey} {...axisProps} tickFormatter={tickFormatter(typeOf(xKey), currency)} />
            <YAxis {...axisProps} width={56} tickFormatter={tickFormatter(yType, currency)} />
            {tooltip}{legend}
            {yKeys.map((key, i) => (
              <Bar key={key} dataKey={key} fill={seriesColor(i)} radius={[4, 4, 0, 0]} maxBarSize={38}>
                {/* Direct labels on a single series — the contrast relief rule. */}
                {yKeys.length === 1 && rows.length <= 12 && (
                  <LabelList
                    dataKey={key}
                    position="top"
                    className="fill-ink-500"
                    fontSize={10}
                    formatter={(v: unknown) => formatCell(v, yType, currency)}
                  />
                )}
              </Bar>
            ))}
          </BarChart>
        );
    }
  })();

  return (
    <div>
      <ViewToggle showTable={false} onToggle={() => setShowTable(true)} />
      <ResponsiveContainer width="100%" height={height}>
        {chart}
      </ResponsiveContainer>
    </div>
  );
}

function ViewToggle({ showTable, onToggle }: { showTable: boolean; onToggle: () => void }) {
  return (
    <div className="mb-1 flex justify-end">
      <button
        onClick={onToggle}
        className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-ink-400 hover:bg-ink-100 hover:text-ink-600"
        title={showTable ? 'Show chart' : 'Show data table'}
      >
        {showTable ? <ChartNoAxesColumn className="h-3.5 w-3.5" /> : <Table2 className="h-3.5 w-3.5" />}
        {showTable ? 'Chart' : 'Data'}
      </button>
    </div>
  );
}
